import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useLogin } from "../../context/Login/LoginContext";
import {
    AboutMe,
    Education,
    LeftSidebar,
    PortfolioCard,
    ProfileHeader,
    Skills,
    WorkHistory,
} from "./components";
import useUserData from "./hooks/useUserData";

type ProfileProps = {
    id?: string;
}

/**
 * Profile page component that displays a user's profile information
 * Allows for viewing, editing, and updating user data
 */
const Profile = ({ id: passId }: ProfileProps) => {
    // Get user id from url params
    const { id: paramId } = useParams();
    const { user } = useLogin();
    const id = paramId || user?._id || "";
    const isOwner = user?._id === id;

    // Use custom hook for user data management
    const {
        userData,
        setUserData,
        skills,
        setSkills,
        loading,
        error,
        fetchUserData,
        updateUserData,
        editMode,
        setEditMode,
    } = useUserData(id);

    // Make sure we re-fetch data when the ID changes
    useEffect(() => {
        if (id) {
            fetchUserData();
        }
    }, [id, fetchUserData]);

    const handleEdit = () => {
        setEditMode(true);
    };

    const handleSave = async () => {
        if (!userData) return;

        const updatedData = {
            ...userData,
            skills: skills.join(",")
        };

        const success = await updateUserData(updatedData);
        if (success) {
            setEditMode(false);
        }
    };

    const handleCancel = () => {
        setEditMode(false);
        fetchUserData();
    };

    // Loading state
    if (loading && !userData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // Error state
    if (error && !userData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    // No data state
    if (!userData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <Typography>User not found</Typography>
            </Box>
        );
    }

    return (
        <Box width="100%" height="100%" overflow="auto">
            <Box sx={{ maxWidth: 1200, margin: "0 auto", padding: 3 }}>
                {/* Profile Header */}
                <ProfileHeader
                    userData={userData}
                    setUserData={setUserData}
                    editMode={editMode}
                    onEdit={handleEdit}
                    onSave={handleSave}
                    onCancel={handleCancel}
                />

                <Grid container spacing={3}>
                    {/* Left Column */}
                    <Grid item xs={12} md={4}>
                        <LeftSidebar
                            userData={userData}
                            setUserData={setUserData}
                            editMode={editMode}
                        />
                    </Grid>

                    {/* Right Column */}
                    <Grid item xs={12} md={8}>
                        <AboutMe
                            userData={userData}
                            setUserData={setUserData}
                            editMode={editMode}
                        />

                        <PortfolioCard
                            userId={id}
                            editMode={editMode}
                            hideDraft={!isOwner}
                        />

                        <Skills
                            skills={skills}
                            setSkills={setSkills}
                            editMode={editMode}
                        />

                        <WorkHistory
                            userData={userData}
                            setUserData={setUserData}
                            editMode={editMode}
                        />

                        <Education
                            userData={userData}
                            setUserData={setUserData}
                            editMode={editMode}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default Profile;